import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { mockData } from '../data/mockData';
import { getPersonalImages } from '../utils/personalImages';

const CardStack = ({ images, isVisible }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showHint, setShowHint] = useState(false);
    const [wiggle, setWiggle] = useState(false);
    const [loadedImages, setLoadedImages] = useState({});
    const hasInteracted = React.useRef(false);

    const handleImageLoad = useCallback((index) => {
        setLoadedImages(prev => ({ ...prev, [index]: true }));
    }, []);

    useEffect(() => {
        if (isVisible && !hasInteracted.current) {
            const hintTimer = setTimeout(() => {
                setShowHint(true);
                setWiggle(true);
            }, 400);

            const hideTimer = setTimeout(() => {
                setShowHint(false);
                setWiggle(false);
            }, 3000);

            return () => {
                clearTimeout(hintTimer);
                clearTimeout(hideTimer);
            };
        }
    }, [isVisible]);

    const handleClick = () => {
        hasInteracted.current = true;
        setShowHint(false);
        setWiggle(false);
        setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    return (
        <div
            className="relative w-full h-full cursor-pointer group"
            onClick={handleClick}
        >
            {images.map((image, index) => {
                const offset = (index - currentIndex + images.length) % images.length;
                const isActive = offset === 0;
                const isNext = offset === 1;
                const isPrev = offset === images.length - 1;

                if (offset > 2 && offset < images.length - 1) return null;

                return (
                    <div
                        key={index}
                        className={`absolute inset-0 rounded-2xl overflow-hidden transition-all duration-500 ease-out shadow-lg border border-border-light ${
                            isActive && wiggle ? 'animate-card-nudge' : ''
                        }`}
                        style={{
                            transform: isActive
                                ? 'translateX(0) rotate(0deg) scale(1)'
                                : isNext
                                    ? 'translateX(8px) translateY(8px) rotate(2deg) scale(0.98)'
                                    : isPrev
                                        ? 'translateX(-8px) translateY(8px) rotate(-2deg) scale(0.98)'
                                        : 'translateX(16px) translateY(16px) rotate(4deg) scale(0.96)',
                            zIndex: isActive ? 30 : isNext ? 20 : isPrev ? 20 : 10,
                            opacity: isActive ? 1 : offset <= 2 || isPrev ? 0.6 : 0,
                        }}
                    >
                        {!loadedImages[index] && (
                            <div className="absolute inset-0 bg-gradient-to-br from-coral-100 to-cream-200 flex flex-col items-center justify-center gap-3 z-10">
                                <div className="w-8 h-8 border-2 border-charcoal-300 border-t-charcoal-600 rounded-full animate-spin" />
                                <p className="text-sm text-charcoal-500 font-medium tracking-wide">Quality takes time</p>
                            </div>
                        )}
                        <img
                            src={image.src}
                            alt={image.alt}
                            onLoad={() => handleImageLoad(index)}
                            className={`w-full h-full object-cover transition-opacity duration-500 ${loadedImages[index] ? 'opacity-100' : 'opacity-0'}`}
                        />
                    </div>
                );
            })}

            {/* Click indicator - auto-shows on entrance, also shows on hover */}
            <div className={`absolute bottom-4 right-4 z-40 flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-xs text-charcoal-600 shadow-sm transition-all duration-500 ${
                showHint ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0'
            }`}>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
                <span>Click to flip</span>
            </div>

        </div>
    );
};

const AboutSection = () => {
    const [activeView, setActiveView] = useState('professional');
    const { beyondOffice } = mockData.personalInfo;

    const personalImages = useMemo(() => getPersonalImages({ shuffle: true }), []);

    return (
        <section className="section relative overflow-hidden">
            {/* Background blob */}
            <div className="absolute -top-20 -left-40 w-[400px] h-[400px] gradient-blob gradient-blob-peach opacity-30" />

            <div className="container relative z-10">
                {/* Section Heading */}
                <h2 className="section-heading text-charcoal-900 mb-12">
                    About <em className="italic-emphasis">me</em>
                </h2>

                {/* Toggle Switch - above grid so it stays on top on mobile */}
                <div className="flex items-center gap-3 mb-8">
                    <button
                        onClick={() => setActiveView('professional')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 border ${
                            activeView === 'professional'
                                ? 'border-charcoal-900 text-charcoal-900 bg-charcoal-900/5'
                                : 'border-charcoal-300 text-charcoal-500 hover:border-charcoal-500 hover:text-charcoal-700'
                        }`}
                    >
                        <span>💼</span>
                        <span>Professional</span>
                    </button>
                    <button
                        onClick={() => setActiveView('beyond')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 border ${
                            activeView === 'beyond'
                                ? 'border-charcoal-900 text-charcoal-900 bg-charcoal-900/5'
                                : 'border-charcoal-300 text-charcoal-500 hover:border-charcoal-500 hover:text-charcoal-700'
                        }`}
                    >
                        <span>🌴</span>
                        <span>Beyond Office</span>
                    </button>
                </div>

                {/* Main Grid */}
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-stretch">
                    {/* Left Side - Content */}
                    <div className="flex flex-col order-2 lg:order-1">
                        {/* Content Area */}
                        <div className="flex-1 relative">
                            {/* Professional View */}
                            <div
                                className={`transition-all duration-400 ease-in-out ${
                                    activeView === 'professional'
                                        ? 'opacity-100 translate-y-0'
                                        : 'opacity-0 translate-y-4 absolute inset-0 pointer-events-none'
                                }`}
                            >
                                <div className="space-y-5">
                                    <p className="text-lg text-charcoal-700 leading-relaxed">
                                        Hello, I'm <span className="text-charcoal-900 font-semibold">{mockData.personalInfo.name}</span>,
                                        a software engineer with 3+ years of experience building clean, user-friendly digital products.
                                    </p>
                                    <p className="text-charcoal-600 leading-relaxed">
                                        My journey into software began with curiosity about how things work, why they break, and how to make 
                                        them better. Over the years, that curiosity turned into hands-on experience across Flutter mobile 
                                        development and React web applications, building products used by thousands of users.
                                    </p>
                                    <p className="text-charcoal-600 leading-relaxed">
                                        I care about clean architecture, maintainable code, and thoughtful user experiences. I enjoy
                                         working closely with the product, understanding actual problems and turning them into reliable, 
                                         scalable solutions.
                                    </p>
                                    <p className="text-charcoal-600 leading-relaxed">
                                        Curious about what I do{' '}
                                        <button
                                            onClick={() => setActiveView('beyond')}
                                            className="underline text-charcoal-800 hover:text-charcoal-900 transition-colors"
                                        >
                                            beyond the office
                                        </button>
                                        ?
                                    </p>

                                    {/* Stats */}
                                    <div className="flex flex-wrap gap-8 pt-4">
                                        <div>
                                            <div className="font-serif text-3xl text-charcoal-900">3+</div>
                                            <div className="text-sm text-charcoal-500">Years Experience</div>
                                        </div>
                                        <div>
                                            <div className="font-serif text-3xl text-charcoal-900">10+</div>
                                            <div className="text-sm text-charcoal-500">Projects Delivered</div>
                                        </div>
                                        <div>
                                            <div className="font-serif text-3xl text-charcoal-900">10K+</div>
                                            <div className="text-sm text-charcoal-500">Users Served</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Beyond Office View */}
                            <div
                                className={`transition-all duration-400 ease-in-out ${
                                    activeView === 'beyond'
                                        ? 'opacity-100 translate-y-0'
                                        : 'opacity-0 translate-y-4 absolute inset-0 pointer-events-none'
                                }`}
                            >
                                <div className="space-y-5">
                                    <p className="text-xl text-charcoal-800 font-medium leading-relaxed">
                                        {beyondOffice.tagline}
                                    </p>

                                    {beyondOffice.description.map((para, index) => (
                                        <p key={index} className="text-charcoal-600 leading-relaxed">
                                            {para}
                                        </p>
                                    ))}

                                    <p className="text-charcoal-700 italic font-medium">
                                        "{beyondOffice.signOff}"
                                    </p>

                                    {/* Interests */}
                                    <div className="flex flex-wrap gap-2 pt-2">
                                        {beyondOffice.interests.map((interest, index) => (
                                            <span
                                                key={index}
                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-cream-200 rounded-full text-sm text-charcoal-700 border border-border-light"
                                            >
                                                <span>{interest.icon}</span>
                                                <span>{interest.name}</span>
                                            </span>
                                        ))}
                                    </div>

                                    {/* Stats */}
                                    <div className="flex flex-wrap gap-8 pt-2">
                                        {beyondOffice.stats.map((stat, index) => (
                                            <div key={index}>
                                                <div className="font-serif text-3xl text-charcoal-900">{stat.value}</div>
                                                <div className="text-sm text-charcoal-500">{stat.label}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Side - Image */}
                    <div className="relative flex items-center order-1 lg:order-2">
                        {activeView === 'professional' ? (
                            <>
                                <div className="w-full aspect-[5/7] max-h-[520px] rounded-2xl overflow-hidden bg-gradient-to-br from-cream-200 to-cream-300 border border-border-light">
                                    <img
                                        src={mockData.personalInfo.avatar}
                                        alt={mockData.personalInfo.name}
                                        className="w-full h-full object-cover object-top"
                                    />
                                </div>
                                {/* Floating availability badge */}
                                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-card border border-border-light">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                            <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-charcoal-900">Available for work</div>
                                            <div className="text-xs text-charcoal-500">Open to opportunities</div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="w-full aspect-[3/4] max-h-[720px]">
                                    <CardStack images={personalImages} isVisible={activeView === 'beyond'} />
                                </div>
                                {/* Floating explorer badge */}
                                <div className="absolute -top-6 -left-6 bg-white rounded-xl p-4 shadow-card border border-border-light z-50">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-coral-100 rounded-full flex items-center justify-center">
                                            <span className="text-lg">🧭</span>
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-charcoal-900">Always exploring</div>
                                            <div className="text-xs text-charcoal-500">New places & ideas</div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AboutSection;
